import * as React from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { DateTimeFormatter } from '../../shared/date-format';
import { BarChartLegendItem } from './stacked-bar-chart';
import lightOrDark from './light-or-dark';

export interface BarChartTooltipContent {
  date: Date;
  data: {
    name: string;
    amount: number;
  }[];
}

interface Props {
  x: number;
  y: number;
  content: BarChartTooltipContent;
  /** width of the entire svg */
  svgWidth: number;
  legend: BarChartLegendItem[];
}

const useStyles = makeStyles<Theme, { x: number; svgWidth: number }>((theme) => ({
  root: {},
  text: ({ x, svgWidth }) => ({
    fontSize: 16,
    dominantBaseline: 'hanging',
    textAnchor: x < svgWidth / 2 ? 'start' : 'end'
  })
}));

/**
 * Render the tooltip in the Stacked bar Chart on hover over bars
 * - Places tooltip the right or left of the corresponding bar depending on where there is more space
 * - Sets the background of data labels to their legend color and determines whether the text
 *   should be dark or light colored for visibility
 */
const BarChartTooltip = ({ x, y, content, svgWidth, legend }: Props) => {
  const classes = useStyles({ x, svgWidth });
  const dateRef = React.useRef<SVGTextElement>();
  const [dateRect, setDateRect] = React.useState<DOMRect>();

  React.useLayoutEffect(() => {
    if (dateRef.current) {
      const bbox = dateRef.current.getBBox();
      setDateRect(bbox);
    }
  }, [dateRef, content, x, y]);

  return (
    <g transform={`translate(${x < svgWidth / 2 ? x + 20 : x - 10},${y})`}>
      {dateRect && (
        <rect
          x={dateRect.x - 5}
          y={dateRect.y}
          width={dateRect.width + 10}
          height={dateRect.height}
          fill="rgba(32,32,32,0.8)"
        />
      )}
      <text className={classes.text} ref={dateRef} fill="white">
        {DateTimeFormatter.format(content.date)}
      </text>
      {dateRect &&
        content.data.map((d, i) => (
          <TextWithBG
            y={dateRect.height * i + dateRect.height}
            content={`${d.name}: ${d.amount}`}
            className={classes.text}
            color={legend.find((label) => label.name == d.name).color}
            key={d.name}
          />
        ))}
    </g>
  );
};

export default BarChartTooltip;

const TextWithBG = ({
  y,
  content,
  className,
  color
}: {
  y: number;
  content: string;
  className: string;
  color: string;
}) => {
  const textRef = React.useRef<SVGTextElement>();
  const [textRect, setTextRect] = React.useState<DOMRect>();

  React.useLayoutEffect(() => {
    if (textRef.current) {
      const bbox = textRef.current.getBBox();
      setTextRect(bbox);
    }
  }, [textRef, content, y]);

  return (
    <React.Fragment>
      {textRect && (
        <rect
          x={textRect.x - 5}
          y={textRect.y}
          width={textRect.width + 10}
          height={textRect.height}
          fill={color}
        />
      )}
      <text
        ref={textRef}
        dy={y}
        className={className}
        fill={lightOrDark(color) == 'light' ? 'black' : 'white'}
      >
        {content}
      </text>
    </React.Fragment>
  );
};
